import { Link } from 'react-router-dom'
import { PublicNavBar } from '../components/PublicNavBar'
import { PublicFooter } from '../components/PublicFooter'
import { usePageMeta } from '../hooks/usePageMeta'
import { pageMeta } from '../seo/pageMeta'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">{title}</h2>
      <div className="space-y-3 text-gray-600 dark:text-gray-300 leading-relaxed text-[15px]">{children}</div>
    </section>
  )
}

export default function Terms() {
  usePageMeta(pageMeta('/terms'))

  return (
    <div className="min-h-screen text-gray-900 dark:text-gray-100">
      <PublicNavBar />

      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-bold mb-2 text-gray-900 dark:text-white">Terms of Service</h1>
        <p className="text-sm text-gray-400 dark:text-gray-500 mb-10">Effective date: July 12, 2026</p>

        <Section title="1. Agreement to these Terms">
          <p>
            Factory Nexus is an AI software development platform operated by Factory Nexus by TynHub
            (&ldquo;Factory Nexus&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). By creating an account or using the
            service, you agree to these Terms of Service and our{' '}
            <Link to="/privacy" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">Privacy Policy</Link>.
            If you use the service on behalf of a company, you confirm you have authority to bind that company to
            these terms.
          </p>
        </Section>

        <Section title="2. Your account">
          <p>
            You must provide accurate information when registering and keep your credentials secure. You are
            responsible for all activity under your account. You must be at least 16 years old to use the service.
            Notify us at{' '}
            <a href="mailto:support@notify.factory-nexus.ai" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">support@notify.factory-nexus.ai</a>{' '}
            if you suspect unauthorized use of your account.
          </p>
        </Section>

        <Section title="3. The service">
          <p>
            Factory Nexus runs AI coding agents that build, review, and modify software projects on your behalf.
            You describe what you want; agents write code, run builds and tests, and prepare changes for your
            review. Output from AI systems can be wrong, insecure, or incomplete — you are responsible for
            reviewing generated code before relying on it in production.
          </p>
        </Section>

        <Section title="4. Acceptable use">
          <p>You agree not to use the service to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>build malware, exploits, or software designed to harm people or systems;</li>
            <li>violate any law, or infringe the intellectual-property or privacy rights of others;</li>
            <li>attempt to breach, probe, or overload our infrastructure or other users&rsquo; projects;</li>
            <li>resell or provide the service to third parties without our written consent;</li>
            <li>circumvent usage limits, billing, or plan restrictions.</li>
          </ul>
          <p>We may suspend or terminate accounts that violate this section.</p>
        </Section>

        <Section title="5. Your content and generated code">
          <p>
            You retain ownership of everything you provide to the service — prompts, repositories, files, and
            project data (&ldquo;your content&rdquo;). <strong className="text-gray-800 dark:text-gray-200">Code generated by Factory Nexus agents for your
            projects belongs to you.</strong> We claim no ownership over your projects or their output. You grant
            us a limited license to store, process, and transmit your content solely to operate the service —
            including sending it to the AI providers described in Section 7.
          </p>
        </Section>

        <Section title="6. GitHub access">
          <p>
            If you connect GitHub, we request OAuth scopes needed to read your profile and email and, when you
            enable repository features, to access the repositories you choose. GitHub tokens are stored encrypted
            and used only to perform actions you initiate — importing repositories, pushing branches, and opening
            pull requests. You can revoke Factory Nexus&rsquo;s access at any time from your GitHub settings.
          </p>
        </Section>

        <Section title="7. AI providers">
          <p>
            When you start an agent task, code review, chat, or estimate, your prompt and the relevant repository
            context are sent to third-party AI providers to generate the response. We currently use Anthropic,
            models routed through OpenRouter, and DigitalOcean serverless inference. Factory Nexus does not use
            your code to train models. See{' '}
            <Link to="/how-we-use-ai" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">How we use AI</Link>{' '}
            for details.
          </p>
        </Section>

        <Section title="8. Anonymous projects">
          <p>
            You can try Factory Nexus without an account. Projects created without signing in are automatically
            and permanently deleted after 7 days, including their files. Sign up and claim a project to keep it.
          </p>
        </Section>

        <Section title="9. Billing and subscriptions">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>The <strong className="text-gray-800 dark:text-gray-200">Free</strong> plan costs nothing and has no time limit.</li>
            <li><strong className="text-gray-800 dark:text-gray-200">Pro</strong> is $19 per month. <strong className="text-gray-800 dark:text-gray-200">Team</strong> is $49 per seat per month. Plans bill monthly through Stripe.</li>
            <li>Each plan includes usage limits (projects, concurrent agents, agent minutes, and similar allowances) published on the <Link to="/pricing" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">pricing page</Link>. When you reach a limit, the related feature pauses until the limit resets or you upgrade — nothing is deleted.</li>
            <li>You can cancel at any time. Paid access continues until the end of the current billing period, after which the account moves to the Free plan. Fees already paid are non-refundable except where required by law.</li>
            <li>We may change prices with at least 30 days&rsquo; notice before the change applies to your next billing period.</li>
          </ul>
        </Section>

        <Section title="10. Termination">
          <p>
            You may delete your account at any time. We may suspend or terminate your access if you materially
            breach these terms, create risk or legal exposure for us, or if we discontinue the service. Where
            practical, we will give you notice and a chance to export your projects first.
          </p>
        </Section>

        <Section title="11. Disclaimers">
          <p>
            The service is provided &ldquo;as is&rdquo; without warranties of any kind, express or implied,
            including fitness for a particular purpose and non-infringement. We do not warrant that generated
            code is correct, secure, or suitable for production, or that the service will be uninterrupted or
            error-free.
          </p>
        </Section>

        <Section title="12. Limitation of liability">
          <p>
            To the maximum extent permitted by law, Factory Nexus will not be liable for indirect, incidental,
            special, or consequential damages, or for loss of profits, data, or goodwill. Our total liability for
            any claim relating to the service is limited to the amount you paid us in the 12 months before the
            claim arose (or $100 if you paid nothing).
          </p>
        </Section>

        <Section title="13. Changes to these terms">
          <p>
            We may update these terms from time to time. For material changes we will give notice — by email or
            in-product — before the changes take effect. Continued use of the service after the effective date
            constitutes acceptance of the updated terms.
          </p>
        </Section>

        <Section title="14. Contact">
          <p>
            Questions about these terms? Email{' '}
            <a href="mailto:support@notify.factory-nexus.ai" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">support@notify.factory-nexus.ai</a>.
          </p>
        </Section>
      </div>

      <PublicFooter />
    </div>
  )
}
